import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import {
	Box,
	IconButton,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	Detail,
	Exemption,
	Person,
	QueryStatus,
	SessionState,
	SettingsObject,
} from "../utils";
import { ExemptionsRow } from "./ExemptionsRow";

interface ExemptionsGridProps {
	people: Person[];
	nameSearch: string;
	sortModel: {
		column: string;
		direction: boolean;
	};
	exemptionsQueryData: { [x: string]: Exemption };
	detailsQueryData: { [x: string]: Detail[] };
	filteredData: { [x: string]: any }[];
	settings: SettingsObject;
	setPostStatus: (arg0: QueryStatus) => void;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setDetailsModal: (arg0: string) => void;
	refetchExemptions: () => void;
	setSortModel: (arg0: { column: string; direction: boolean }) => void;
}

export function ExemptionsGrid({
	people,
	nameSearch,
	sortModel,
	filteredData,
	exemptionsQueryData,
	detailsQueryData,
	settings,
	setPostStatus,
	sessionState,
	setSessionState,
	setDetailsModal,
	refetchExemptions,
	setSortModel,
}: ExemptionsGridProps) {
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(20);

	const maxPages = Math.ceil(filteredData.length / pageSize);

	useEffect(() => {
		if (maxPages <= page) {
			setPage(Math.max(0, maxPages - 1));
		}
	}, [pageSize, nameSearch]);

	const currentCases = filteredData.filter(
		(_: any, i: number) => i >= page * pageSize && i < (page + 1) * pageSize
	);

	return (
		<>
			<TableContainer
				sx={{
					maxHeight: `calc(100vh - ${
						document
							.getElementById("datagrid-start")
							?.getBoundingClientRect().top
					}px - 84px)`,
				}}
			>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell></TableCell>
							<TableCell
								sortDirection={
									sortModel.column === "date"
										? sortModel.direction
											? "desc"
											: "asc"
										: false
								}
							>
								<TableSortLabel
									active={sortModel.column === "date"}
									direction={
										sortModel.direction ? "desc" : "asc"
									}
									onClick={() =>
										setSortModel({
											column: "date",
											direction: sortModel.direction
												? !sortModel.direction
												: true,
										})
									}
								>
									Case Opened
								</TableSortLabel>
							</TableCell>
							<TableCell
								sortDirection={
									sortModel.column === "client"
										? sortModel.direction
											? "desc"
											: "asc"
										: false
								}
							>
								<TableSortLabel
									active={sortModel.column === "client"}
									direction={
										sortModel.direction ? "desc" : "asc"
									}
									onClick={() =>
										setSortModel({
											column: "client",
											direction: sortModel.direction
												? !sortModel.direction
												: true,
										})
									}
								>
									Client Name
								</TableSortLabel>
							</TableCell>
							<TableCell
								sortDirection={
									sortModel.column === "attorney"
										? sortModel.direction
											? "desc"
											: "asc"
										: false
								}
							>
								<TableSortLabel
									active={sortModel.column === "attorney"}
									direction={
										sortModel.direction ? "desc" : "asc"
									}
									onClick={() =>
										setSortModel({
											column: "attorney",
											direction: sortModel.direction
												? !sortModel.direction
												: true,
										})
									}
								>
									Attorney(s)
								</TableSortLabel>
							</TableCell>
							<TableCell
								sortDirection={
									sortModel.column === "staff"
										? sortModel.direction
											? "desc"
											: "asc"
										: false
								}
							>
								<TableSortLabel
									active={sortModel.column === "staff"}
									direction={
										sortModel.direction ? "desc" : "asc"
									}
									onClick={() =>
										setSortModel({
											column: "staff",
											direction: sortModel.direction
												? !sortModel.direction
												: true,
										})
									}
								>
									Staff
								</TableSortLabel>
							</TableCell>
							<TableCell>Application</TableCell>
							<TableCell>Submitted</TableCell>
							<TableCell
								sortDirection={
									sortModel.column === "status"
										? sortModel.direction
											? "desc"
											: "asc"
										: false
								}
							>
								<TableSortLabel
									active={sortModel.column === "status"}
									direction={
										sortModel.direction ? "desc" : "asc"
									}
									onClick={() =>
										setSortModel({
											column: "status",
											direction: sortModel.direction
												? !sortModel.direction
												: true,
										})
									}
								>
									Status
								</TableSortLabel>
							</TableCell>
							<TableCell>Notes</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{currentCases.map((datum: { [x: string]: any }) => {
							const {
								application = settings
									.exemptionApplicationTypes[0],
								status = settings.exemptionStatusTypes[0],
								submitted,
							} = exemptionsQueryData[datum["_id"]] || {};
							return (
								<ExemptionsRow
									datum={datum}
									application={application}
									status={status}
									submitted={submitted}
									detailsQueryData={detailsQueryData}
									settings={settings}
									people={people}
									sessionState={sessionState}
									setSessionState={setSessionState}
									setPostStatus={setPostStatus}
									setDetailsModal={setDetailsModal}
									refetchExemptions={refetchExemptions}
								/>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<Box
				sx={{
					border: (theme) => `1px solid ${theme.palette.grey[800]}`,
					height: "45px",
					borderRadius: "4px",
					position: "absolute",
					bottom: "-55px",
					width: "calc(100vw - 234px)",
					display: "flex",
					alignItems: "center",
					justifyContent: "right",
				}}
			>
				<IconButton
					sx={{ float: "right", m: 1 }}
					size="small"
					onClick={() => page > 0 && setPage(page - 1)}
				>
					<ArrowBackIos />
				</IconButton>
				<Typography
					sx={{
						float: "right",
						color: (theme) => theme.palette.common.white,
					}}
				>
					{page + 1}/{maxPages}
				</Typography>
				<IconButton
					size="small"
					sx={{ float: "right", m: 1 }}
					onClick={() => page < maxPages - 1 && setPage(page + 1)}
				>
					<ArrowForwardIos />
				</IconButton>
				<Select
					sx={{ m: 1, p: 0, fontSize: "0.75rem" }}
					size="small"
					value={pageSize}
					onChange={(e) => setPageSize(Number(e.target.value))}
				>
					<MenuItem value={10}>10</MenuItem>
					<MenuItem value={20}>20</MenuItem>
					<MenuItem value={50}>50</MenuItem>
					<MenuItem value={100}>100</MenuItem>
				</Select>
			</Box>
		</>
	);
}
